/**
 * Findings — deficiencies needing action (IA spec §6.3).
 *
 * The no-close rule, structural: this page has NO mutation path — it never
 * imports or calls api.post (a source-level test pins that), and no control
 * with resolve/close semantics exists (a DOM-level test pins that). The
 * screen says why, once, plainly: a finding's state is derived from evidence
 * at the next assessment; remediation is recordable as an entity-supplied
 * fact; no button changes evidence.
 *
 * Severity never renders without its basis.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { errorDetail } from '../services/api';
import { fmtTs } from '../utils/complianceFormat';

const SEV_STYLES = {
  critical: 'bg-red-900/60 text-red-200 border-red-600',
  high: 'bg-red-900/40 text-red-300 border-red-700',
  medium: 'bg-amber-900/40 text-amber-300 border-amber-700',
  low: 'bg-gray-700/40 text-gray-300 border-gray-600',
};

export default function ComplianceFindings() {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get('/skills/compliance/findings');
      if (!mounted.current) return;
      setDoc(data);
    } catch (err) {
      if (!mounted.current) return;
      setError(errorDetail(err, 'Findings could not be loaded.'));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-gray-900 text-gray-100 min-h-screen">
      <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-white mb-1">Findings</h1>
      {doc && (
        <p data-testid="no-close-statement" className="text-xs text-gray-400 mb-5">
          {doc.no_close_statement}
        </p>
      )}

      {loading && <div className="text-gray-400 text-sm">Loading findings…</div>}
      {error && (
        <div data-testid="findings-error" className="bg-red-900/30 border border-red-800 rounded-xl p-5 text-sm text-red-200">
          {error}
          <button onClick={load} className="ml-3 underline">Retry</button>
        </div>
      )}

      {!loading && !error && doc && doc.findings.length === 0 && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 text-sm text-gray-300">
          No findings in the latest assessments. This reflects the assessed scope —
          see Coverage for what is and is not assessed.
        </div>
      )}

      {!loading && !error && doc && doc.findings.map((f) => (
        <div key={`${f.requirement_id}-${f.finding_id}`}
             data-testid={`finding-${f.finding_id}`}
             className="bg-gray-800 rounded-xl border border-gray-700 p-5 mb-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white">{f.condition}</div>
              <div className="text-xs text-gray-400 mt-1">
                <Link to={`/compliance/requirements/${f.requirement_id}`}
                      className="font-mono underline">{f.requirement_id}</Link>
                {' · '}assessed as of {fmtTs(f.assessed_as_of)}
              </div>
              <div className="text-xs text-gray-300 mt-2">Risk: {f.risk_statement}</div>
              <div className="text-xs text-gray-300 mt-1">
                Recommended remediation: {f.remediation_recommended || '—'}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                Verification: {f.verification_needed}
              </div>
              {f.evidence_ids.length > 0 && (
                <div className="text-xs text-gray-500 mt-1 font-mono">
                  Evidence: {f.evidence_ids.join(', ')}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className={`inline-block px-2 py-0.5 text-[11px] rounded-full border ${SEV_STYLES[(f.severity || '').toLowerCase()] || SEV_STYLES.low}`}>
                {f.severity}
              </span>
              <span data-testid={`sev-basis-${f.finding_id}`}
                    className="text-[10px] text-gray-500 text-right max-w-[16rem]">
                {f.severity_basis}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
    </div>
  );
}
